// Caller history fetch + outcome classification (D26, 2026-09-18).
//
// The tenant comes from the CONTACT (its own `tenantId` attribute, set by the
// tenant router on inbound, the outbound bind on outbound, and the booking
// Lambda on callback tasks) — never from a build-time constant. HistoryPanel
// used `VITE_TENANT_ID || 'knktr-live'`, which deploy.sh never sets, so the
// tenant baked into the live bundle depended on which checkout ran the build
// (a gitignored ccp/.env said aurora-dev; a worktree has no .env and fell back
// to knktr-live) — it flipped at least seven times between 28 Aug and 18 Sep,
// and every call fetched that one tenant's history whatever its own tenant.
// Same bug class PR #76 fixed for the Task list.
//
// Pure: no React, no import.meta.env. The panel passes the endpoint, fetch and
// header source in, so scripts/verify-caller-history.mjs runs this under node.
//
// Outcomes are kept distinct on purpose: "no tenant" and "failed" must never
// render as "No previous contacts on record" — that sentence is a claim about
// the caller, and a 403 or a missing tenant says nothing about the caller.

export const NO_TENANT = 'no-tenant';
export const ERROR = 'error';
export const EMPTY = 'empty';
export const OK = 'ok';

// contactId: the server only answers for the client of a contact this agent
// is on (backlog 87). Without it the request is refused.
export function historyUrl(dataUrl, tenantId, phone, contactId) {
  return `${dataUrl}?type=history&tenantId=${encodeURIComponent(tenantId)}`
    + `&caller=${encodeURIComponent(phone)}`
    + (contactId ? `&contactId=${encodeURIComponent(contactId)}` : '');
}

// -> { state, rows, error }. Never throws.
export async function loadCallerHistory({ dataUrl, tenantId, phone, contactId, fetchFn, getHeaders }) {
  const tenant = String(tenantId || '').trim();
  if (!tenant) return { state: NO_TENANT, rows: [], error: null };
  if (!dataUrl) {
    return { state: ERROR, rows: [], error: 'History endpoint not configured (VITE_DATA_URL).' };
  }
  let res;
  try {
    res = await fetchFn(historyUrl(dataUrl, tenant, phone, contactId), { headers: await getHeaders() });
  } catch {
    return { state: ERROR, rows: [], error: 'Could not reach the history service.' };
  }
  if (res.status === 403) {
    // The reader's entitlement gate: this agent is not in the contact's
    // tenant group. Say so — an empty list here would read as "new caller".
    return { state: ERROR, rows: [], error: `Not authorised to view ${tenant} history (403).` };
  }
  if (!res.ok) {
    return { state: ERROR, rows: [], error: `History failed to load (HTTP ${res.status}).` };
  }
  let data;
  try {
    data = await res.json();
  } catch {
    return { state: ERROR, rows: [], error: 'History response was unreadable.' };
  }
  if (!Array.isArray(data)) {
    return { state: ERROR, rows: [], error: 'History response was not a list.' };
  }
  return { state: data.length ? OK : EMPTY, rows: data, error: null };
}

// Caller-history fields (2026-09-22, vault #224). The reader's durationSec is
// the whole contact (AI + queue + agent). Formatted m:ss, or h:mm:ss past an
// hour; anything missing or not a sane number shows nothing rather than 0:00.
export function fmtDuration(sec) {
  const n = Number(sec);
  if (sec === '' || sec == null || !Number.isFinite(n) || n < 0) return '';
  const s = Math.round(n);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// Storm's ruling (2026-09-22, vault #227): no duration on callback TASK rows.
// For a task, Connect's "whole contact" runs from when the callback was booked
// to when it was worked: a waiting time, not a call length, and it can be days.
// The stored value stays as it is; it is only not shown under "duration".
// Surfacing wait time would be its own deliberate feature, not this label.
export function rowDuration(row) {
  if (String(row?.channel || '').toUpperCase() === 'TASK') return '';
  return fmtDuration(row?.durationSec);
}
